import s from "./styles.module.scss";
import Button from "../button";
import Modal from "../modal";
import { useLocation, useNavigate } from "react-router-dom";
import { SignedIn, SignedOut, UserButton, useUser } from "@clerk/clerk-react";
import { useState, useEffect } from "react";
import logo from "../../assets/logo.svg";
import { ReactComponent as ProfileIcon } from "../../assets/profile.svg";
import { ReactComponent as CoursesIcon } from "../../assets/courses.svg";
import { ReactComponent as CalendarIcon } from "../../assets/calendaricon.svg";
import { ReactComponent as ConsoleIcon } from "../../assets/console.svg";
import { ReactComponent as UploadIcon } from "../../assets/uploadSidebar.svg";
import { ReactComponent as ChevronRightIcon } from "../../assets/chevron-right.svg";
import { useSidebar } from "../../contexts/sidebar-context";

// Routes that require confirmation before exit
const ROUTES_REQUIRING_CONFIRMATION = [
  "/select-calendar",
  "/choose-sections",
  "/select-sections",
  "/timezone-selection",
  "/add-course-times",
  "/add-events",
];

// Hamburger icon component (inline)
const HamburgerIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M3 6H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const Header = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useUser();
  const { isCollapsed, isResponsivelyHidden, toggleSidebar } = useSidebar();
  const [isAnimating, setIsAnimating] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const isCalendarPage = pathname === "/calendar";

  // Track window resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if current route requires exit confirmation
  const requiresConfirmation = ROUTES_REQUIRING_CONFIRMATION.some(route =>
    pathname === route || pathname.startsWith(route + "/")
  );

  // Check for first calendar visit in session and trigger animation
  useEffect(() => {
    if (isCalendarPage) {
      const hasVisitedCalendar = sessionStorage.getItem("visited_calendar");
      if (!hasVisitedCalendar) {
        setIsAnimating(true);
        sessionStorage.setItem("visited_calendar", "true");
        // Remove animating state after animation completes
        const timer = setTimeout(() => setIsAnimating(false), 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [isCalendarPage]);

  // Clear all course flow progress from storage
  const clearCourseFlowProgress = () => {
    localStorage.removeItem("allSectionsData");
    localStorage.removeItem("selectedSections");
    localStorage.removeItem("googleAuthInProgress");
    localStorage.removeItem("selectedCalendarType");
    sessionStorage.removeItem("allSectionsData");
    sessionStorage.removeItem("selectedSections");
  };

  const handleLogoClick = () => {
    if (requiresConfirmation) {
      setShowExitModal(true);
    } else {
      navigate("/calendar");
    }
  };

  const handleConfirmExit = () => {
    clearCourseFlowProgress();
    setShowExitModal(false);
    navigate("/calendar");
  };

  const handleCancelExit = () => {
    setShowExitModal(false);
  };

  const isIsSignUp = pathname === "/sign-up";

  const handleSignUp = () => {
    navigate(isIsSignUp ? "/sign-in" : "/sign-up");
  };

  const headerClass = `${s.header} ${isCalendarPage ? s.headerCompact : ''} ${isAnimating ? s.headerAnimating : ''}`;

  return (
    <>
    <header className={headerClass}>
      <div className={`${s.logoContainer} ${isAnimating ? s.shimmer : ''}`} onClick={handleLogoClick}>
        <img src={logo} alt="Beta" className={s.headerLogo} />
        <p>Ocean</p>
      </div>
      <div className={`${s.settingsContainer} ${isAnimating ? s.shimmer : ''}`}>
        {/* Clickable profile picture - different menus for mobile vs desktop */}
        <SignedIn>
          {isMobile ? (
            <UserButton
              key="mobile-user-button"
              afterSignOutUrl="/"
              appearance={{
                elements: {
                  avatarBox: s.userButtonAvatar,
                  userButtonPopoverCard: s.userButtonPopoverCard,
                  userButtonPopoverActionButton: s.popoverActionButton,
                  userButtonPopoverCustomItemButton: s.popoverCustomItemButton,
                  userButtonPopoverActions: s.popoverActions,
                  userPreview: s.userPreview,
                  userPreviewMainIdentifierText: s.identifierText,
                  userPreviewSecondaryIdentifier: s.secondaryIdentifierText,
                  userButtonPopoverActionButton__manageAccount: s.hide,
                  userButtonPopoverActionButtonIcon: s.signOutIcon,
                  userButtonPopoverActionButtonIconBox: s.signOutIconBox,
                },
              }}
              userProfileMode="navigation"
              userProfileUrl="/user-profile"
            >
              <UserButton.MenuItems>
                <UserButton.Link
                  label="Calendar"
                  labelIcon={<CalendarIcon />}
                  href="/calendar"
                />
                <UserButton.Link
                  label="Courses"
                  labelIcon={<CoursesIcon />}
                  href="/courses"
                />
                <UserButton.Link
                  label="Console"
                  labelIcon={<ConsoleIcon />}
                  href="/console"
                />
                <UserButton.Link
                  label="Upload"
                  labelIcon={<UploadIcon />}
                  href="/upload"
                />
                <UserButton.Link
                  label="Profile"
                  labelIcon={<ProfileIcon />}
                  href="/user-profile"
                />
              </UserButton.MenuItems>
            </UserButton>
          ) : (
            <UserButton
              key="desktop-user-button"
              afterSignOutUrl="/"
              appearance={{
                elements: {
                  avatarBox: s.userButtonAvatar,
                  userButtonPopoverCard: s.userButtonPopoverCard,
                  userButtonPopoverActionButton: s.popoverActionButton,
                  userButtonPopoverCustomItemButton: s.popoverCustomItemButton,
                  userButtonPopoverActions: s.popoverActions,
                  userPreview: s.userPreview,
                  userPreviewMainIdentifierText: s.identifierText,
                  userPreviewSecondaryIdentifier: s.secondaryIdentifierText,
                  userButtonPopoverActionButton__manageAccount: s.hide,
                  userButtonPopoverActionButtonIcon: s.signOutIcon,
                  userButtonPopoverActionButtonIconBox: s.signOutIconBox,
                },
              }}
              userProfileMode="navigation"
              userProfileUrl="/user-profile"
            >
              <UserButton.MenuItems>
                <UserButton.Link
                  label="Profile"
                  labelIcon={<ProfileIcon />}
                  href="/user-profile"
                />
              </UserButton.MenuItems>
            </UserButton>
          )}
        </SignedIn>

        {/* Sidebar toggle button - only show on desktop when sidebar is visible */}
        <SignedIn>
          {!isResponsivelyHidden && (
            <button 
              className={s.sidebarToggle} 
              onClick={toggleSidebar}
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? <HamburgerIcon /> : <ChevronRightIcon />}
            </button>
          )}
        </SignedIn>
        
        <SignedOut>
          <Button onClick={handleSignUp} size="medium">
            {isIsSignUp ? "Sign in" : "Create account"}
          </Button>
        </SignedOut>
      </div>
    </header>

    <Modal
      isOpen={showExitModal}
      onClose={handleCancelExit}
      title="Exit course setup?"
      message="You'll lose all progress in the course addition flow. Are you sure you want to exit?"
      type="warning"
      primaryButtonText="Exit"
      secondaryButtonText="Cancel"
      onPrimaryClick={handleConfirmExit}
      onSecondaryClick={handleCancelExit}
    />
    </>
  );
};

export default Header;
